// BALANCE SHEET v2 — the pieces (UI rebuild batch 5). The rules and words are today's (screens/BalanceSheet.tsx),
// copied, not re-derived: "—" never means zero; a line the sheet could not value names WHICH asset ("XRP not valued");
// a line that is part estimate says so on the line; the native per-asset amounts stand beside the derived AED; the
// red proof line when the sheet does not close or could not be checked; the stated-price table unread is a failed
// read, never "no estimates". What moved is where they stand: the lines of a section open behind its row (a detail
// sheet), the rates, stated prices, notes and the rules behind one "Basis & supporting detail" fold.
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { ReportTable, type ReportRow } from '../../ReportTable';
import { DetailSheet, StateLine, TAP } from '../kit';
import type { BalanceSheetLine, BalanceSheetNote, BalanceSheetResponse, BalanceSheetSection } from '../../../lib/api';
import { displayAsset, fmtAge, fmtAmount, fmtDate, fmtRate } from '../../../lib/format';
import { valuationSourceName } from '../../../lib/naming';

// ── what the sheet could not value (copied from screens/BalanceSheet.tsx) ─────────────────────────────────────

/** WHAT THE SHEET COULD NOT VALUE, BY NAME. The Worker names the assets in its `unpriced_assets` note ("…no stated
 *  valuation for XRP — that balance is excluded…") and on a position part's `unpriced`. A miss on the sentence (its
 *  wording moved) names nobody: the line then says "Not valued" plain — never nothing. */
export function unvaluedAssets(data: BalanceSheetResponse): string[] {
  const out = new Set<string>();
  for (const n of data.notes) {
    if (n.code !== 'unpriced_assets') continue;
    const m = /valuation for (.+?) — (?:that balance is|those balances are) excluded/.exec(n.message);
    for (const a of (m?.[1] ?? '').split(', ')) if (a.trim()) out.add(a.trim());
  }
  for (const sec of data.sections) for (const l of sec.lines) for (const p of l.parts ?? []) for (const a of p.unpriced ?? []) out.add(displayAsset(a));
  return [...out];
}

/** The assets of one posted line the sheet could not value — [] when the line is valued. */
function lineUnvalued(line: BalanceSheetLine, unvalued: string[]): string[] | null {
  if (!line.posted || line.aed !== null) return null;
  return Object.entries(line.amounts).filter(([, v]) => v !== 0).map(([a]) => displayAsset(a)).filter((a) => unvalued.includes(a));
}

/** A SECTION'S VALUATION STATE, said beside its figure: what it could not value (by name), or that part of it is an
 *  estimate from a stated price, or nothing. */
export function sectionState(section: BalanceSheetSection, unvalued: string[]): { tone: 'warn' | 'hint'; words: string } | null {
  const missing = new Set<string>();
  let unnamed = false;
  for (const l of section.lines) {
    const u = lineUnvalued(l, unvalued);
    if (u === null) continue;
    if (u.length === 0) unnamed = true;
    for (const a of u) missing.add(a);
  }
  if (missing.size > 0) return { tone: 'warn', words: `${[...missing].join(', ')} not valued` };
  if (unnamed || (section.total_aed === null && section.lines.some((l) => l.posted))) return { tone: 'warn', words: 'Not valued' };
  if (section.lines.some((l) => l.posted && (l.stated?.length ?? 0) > 0)) return { tone: 'hint', words: 'Part estimate' };
  return null;
}

/** The section's lines in a few words, from the lines themselves: "Cash, Exchanges +4". */
export function sectionSummary(section: BalanceSheetSection): string {
  const posted = section.lines.filter((l) => l.posted).map((l) => l.label);
  if (posted.length === 0) return 'Nothing posted';
  const shown = posted.slice(0, 2).join(', ');
  return posted.length > 2 ? `${shown} +${posted.length - 2}` : shown;
}

// ── the figures ────────────────────────────────────────────────────────────────────────────────────────────────

/** The reporting-currency figure. Absent is an em dash, never a zero. */
export function Aed({ value, strong = false, signed = false, unit = false }: {
  value: number | null; strong?: boolean; signed?: boolean; unit?: boolean;
}) {
  if (value === null) return <span className="text-tg-hint">—</span>;
  const colour = signed ? (value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : '') : (value < 0 ? 'text-neg' : '');
  return (
    <span className={`font-mono tabular ${strong ? 'font-semibold' : ''} ${colour}`}>
      {fmtAmount(value, 'AED')}
      {unit && <span className="ml-1 font-sans text-meta font-normal text-tg-hint">AED</span>}
    </span>
  );
}

/** A line that holds AED and nothing else prints its amount once (the AED column), compared AS PRINTED. */
function echoesAed(amounts: Record<string, number>, aed: number | null): boolean {
  const entries = Object.entries(amounts).filter(([, v]) => v !== 0);
  const only = entries[0];
  if (entries.length !== 1 || !only || only[0] !== 'AED' || aed === null) return false;
  return fmtAmount(only[1], 'AED') === fmtAmount(aed, 'AED');
}

/** The native per-asset amounts behind a line — the reading that is always true. Separated by a gap, never a dot. */
function Native({ amounts }: { amounts: Record<string, number> }) {
  const entries = Object.entries(amounts).filter(([, v]) => v !== 0);
  if (entries.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-x-3 text-meta text-tg-hint whitespace-normal">
      {entries.map(([asset, v]) => (
        <span key={asset} className="whitespace-nowrap"><span className="font-mono tabular">{fmtAmount(v, asset)}</span> {displayAsset(asset)}</span>
      ))}
    </span>
  );
}

function lineName(line: BalanceSheetLine, unvalued: string[]) {
  const missing = lineUnvalued(line, unvalued);
  return (
    <div className="whitespace-normal">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={line.posted ? '' : 'text-tg-hint'}>{line.label}</span>
        {!line.posted && <span className="text-meta text-tg-hint">never posted</span>}
      </div>
      {line.posted && !echoesAed(line.amounts, line.aed) && <Native amounts={line.amounts} />}
      {line.posted && (line.stated?.length ?? 0) > 0 && (
        <div className="mt-0.5 text-meta text-tg-hint">Part estimate · {line.stated!.map(displayAsset).join(', ')} at stated prices</div>
      )}
      {missing && <div className="mt-0.5 text-meta text-warn">{missing.length ? `${missing.join(', ')} not valued` : 'Not valued'}</div>}
      {/* words:ok — the line's caveat is the Worker's own sentence, read where the line is opened */}
      {line.posted && line.caveat && <div className="mt-0.5 text-meta text-tg-hint">{line.caveat}</div>}
    </div>
  );
}

/** One line of the sheet as report rows: the line, then its parts (the open position's), muted and never re-added. */
function lineRows(line: BalanceSheetLine, unvalued: string[]): ReportRow[] {
  const main: ReportRow = {
    key: line.key,
    cells: { line: lineName(line, unvalued), aed: <Aed value={line.posted ? line.aed : null} signed={line.key === 'equity.position'} /> },
  };
  const parts: ReportRow[] = (line.parts ?? []).map((p) => ({
    key: `${line.key}:${p.key}`,
    kind: 'sub',
    cells: {
      line: (
        <div className="pl-4 whitespace-normal">
          <span className="text-meta text-tg-hint" title={p.label}>{p.label.replace(/\s*\([^)]*\)/g, '')}</span>
          <span className="ml-1.5 text-meta text-tg-hint opacity-70">{p.txns} {p.txns === 1 ? 'entry' : 'entries'}</span>
          {!echoesAed(p.amounts, p.aed) && <Native amounts={p.amounts} />}
          {p.unpriced?.length ? <div className="text-meta text-warn">{p.unpriced.map(displayAsset).join(', ')} not valued</div> : null}
        </div>
      ),
      aed: p.aed === null
        ? <span className="text-tg-hint">—</span>
        : <span className="font-mono tabular text-meta text-tg-hint">{fmtAmount(p.aed, 'AED')}</span>,
    },
  }));
  return [main, ...parts];
}

/** ONE SECTION OPENED: its lines with their native amounts, estimates, unvalued assets and parts, and its total. */
export function SectionDetail({ section, unvalued, onClose }: { section: BalanceSheetSection; unvalued: string[]; onClose: () => void }) {
  const rows: ReportRow[] = [
    ...section.lines.flatMap((l) => lineRows(l, unvalued)),
    { key: 'total', kind: 'total', cells: { line: `Total ${section.label.toLowerCase()}`, aed: <Aed value={section.total_aed} strong /> } },
  ];
  return (
    <DetailSheet title={section.label} onClose={onClose}>
      <ReportTable
        columns={[
          { key: 'line', label: 'Line', align: 'left', nowrap: false },
          { key: 'aed', label: 'AED' },
        ]}
        rows={rows}
      />
    </DetailSheet>
  );
}

// ── the warnings ───────────────────────────────────────────────────────────────────────────────────────────────

/** Notes the screen already says in its own place: the unvalued asset (the proof line and its row), the unchecked
 *  sheet (the proof line), and a running account below zero (allowed — the books never refuse it). */
const SAID_ELSEWHERE = new Set(['unpriced_assets', 'presentation_uncheckable', 'negative_custody']);

/** Does a note name a figure — "differ by 1,234.00 AED"? */
function namesAmount(message: string): boolean {
  return /\d[\d,]*(?:\.\d+)?\s*(?:AED|INR|USDT)\b|\b(?:AED|INR|USDT)\s*\d/.test(message);
}

/** THE WARNINGS THAT STAY IN VIEW, one line each: a gap the proof line does not already say, a valuation whose two
 *  rates disagree, and any other warning that names a figure. The rest read whole in Basis & supporting detail. */
export function pinnedWarnings(data: BalanceSheetResponse): BalanceSheetNote[] {
  const barSaysGap = !data.check.closes && data.check.presentation_gap_aed !== null;
  return data.notes.filter((n) => n.tone === 'warn' && !SAID_ELSEWHERE.has(n.code)
    && !(barSaysGap && n.code === 'presentation_gap')
    && (n.code === 'rate_disagreement' || n.code === 'presentation_gap' || namesAmount(n.message)));
}

/** A WARNING IS ONE SENTENCE — the fact and its figures; the Worker's whole sentence is behind the tap. */
export function shortWarning(n: BalanceSheetNote): string {
  if (n.code === 'rate_disagreement') {
    const m = /^(\S+) is valued at the (stated price|desk's own dealt rate) \((\d[\d.]*) AED[^)]*\), which is (\d+%) away from the ([^(]+?) \((\d[\d.]*) AED[^)]*\)\./.exec(n.message);
    if (!m) return n.message;
    const stated = m[2] === 'stated price';
    const [first, second] = stated ? ['stated', 'dealt'] : ['dealt', 'stated'];
    return `${m[1]} at ${first} ${fmtRate(m[3], 'AED')} AED, ${second} ${fmtRate(m[6], 'AED')} AED — ${m[4]} apart.`;
  }
  return n.message.split(/(?<=\.)\s+/)[0] ?? n.message;
}

/** THE PROOF, red and first when the sheet does not close — `closes` is false when it could not be CHECKED as well as
 *  when it fails (unverified is not a pass). One line; the rule behind it one tap down. */
export function ProofLine({ data, unvalued }: { data: BalanceSheetResponse; unvalued: string[] }) {
  if (data.check.closes) return null;
  const gap = data.check.presentation_gap_aed;
  return gap === null ? (
    <StateLine tone="bad" more="A line the sheet could not value is left out of its AED figure, so assets, liabilities and equity cannot be proved equal.">
      Not proved to balance — {unvalued.length > 0 ? `${unvalued.join(', ')} not valued` : 'a line is unvalued'}; totals provisional.
    </StateLine>
  ) : (
    <StateLine tone="bad" more={`Assets − liabilities and equity differ by ${fmtAmount(gap, 'AED')} AED.`}>
      This sheet does not balance — off by {fmtAmount(gap, 'AED')} AED.
    </StateLine>
  );
}

// ── basis & supporting detail ──────────────────────────────────────────────────────────────────────────────────

function RateRow({ pair, note, figure }: { pair: string; note?: ReactNode; figure: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 border-b border-stroke-soft py-2 last:border-b-0">
      <span className="min-w-0 text-body">{pair}</span>
      <span className="font-mono tabular text-body">{figure}</span>
      {note != null && <span className="basis-full text-meta text-tg-hint">{note}</span>}
    </div>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h3 className="mb-1 mt-4 text-label font-medium uppercase tracking-wider text-tg-hint first:mt-0">{children}</h3>;
}

/** WHAT THE AED COLUMN LEANS ON, and the notes behind the sheet — folded, as Astra's "Basis & supporting detail". */
export function Basis({ data }: { data: BalanceSheetResponse }) {
  const { rates } = data;
  const stated = rates.stated ?? [];
  const first = stated[0];
  const shared = !!first && stated.length > 1 && stated.every((s) => s.source === first.source && s.as_of === first.as_of);
  const provenance = (s: (typeof stated)[number]) => <>{valuationSourceName(s.source)} · {fmtDate(s.as_of)}{fmtAge(s.as_of) && ` · ${fmtAge(s.as_of)}`}</>;
  const quote = (pair: string) => pair.split('/')[1];
  const warnings = data.notes.filter((n) => n.tone === 'warn');
  const info = data.notes.filter((n) => n.tone !== 'warn');
  const hasRates = rates.pairs.length > 0 || !!rates.cross || rates.pegged.length > 0;
  return (
    <details className="group mt-4 border-t border-stroke pt-4 lg:mt-5">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-1 text-body text-tg-hint lg:min-h-[40px] [&::-webkit-details-marker]:hidden"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />Basis &amp; supporting detail</summary>
      <div className="mt-2 rounded-[10px] border border-stroke bg-panel p-4 text-body lg:max-w-3xl lg:p-5">
        <p className="text-tg-hint">
          What the desk owns, owes and has left over, at this instant. Every line carries its own currencies; the AED
          figure is derived from the rates below — this desk&rsquo;s own trades (the median), not a market feed.
        </p>
        {hasRates && (
          <>
            <Heading>Rates</Heading>
            {rates.pairs.map((p) => (
              <RateRow key={p.pair} pair={p.pair} figure={fmtRate(p.median, quote(p.pair))}
                note={<>{p.trades} {p.trades === 1 ? 'trade' : 'trades'} · {fmtRate(p.low, quote(p.pair))}–{fmtRate(p.high, quote(p.pair))}</>} />
            ))}
            {rates.cross && <RateRow pair={rates.cross.pair} figure={rates.cross.inr_per_aed_display} note={rates.cross.derivation} />}
            {rates.pegged.map((p) => <RateRow key={p.pair} pair={p.pair} figure={fmtRate(p.rate, quote(p.pair))} note={p.basis} />)}
          </>
        )}
        {stated.length > 0 && (
          <>
            <Heading>Stated valuations</Heading>
            <p className="text-meta text-tg-hint">
              Prices somebody named and dated, not ones this desk dealt; every figure leaning on one is an estimate.
              {shared && first && <> {provenance(first)}.</>}
            </p>
            {stated.map((s) => (
              <RateRow key={`${s.asset}|${s.quote}`} pair={`${displayAsset(s.asset)}/${s.quote}`} figure={fmtRate(s.rate, s.quote)} note={shared ? null : provenance(s)} />
            ))}
          </>
        )}
        {(warnings.length > 0 || info.length > 0) && (
          <>
            <Heading>Notes</Heading>
            <ul className="space-y-2">
              {warnings.map((n) => <li key={n.code + n.message.slice(0, 12)} className="text-warn">{n.message}</li>)}
              {info.map((n) => <li key={n.code + n.message.slice(0, 12)} className="text-tg-hint">{n.message}</li>)}
            </ul>
          </>
        )}
      </div>
    </details>
  );
}

/** THE STATED-PRICE TABLE COULD NOT BE READ: a failed read, never "no estimates were used" — in view, with Try again. */
export function StatedUnread({ onRetry }: { onRetry: () => void }) {
  return (
    <StateLine tone="warn" more="A failed read, not a settled fact about those holdings."
      action={<button type="button" className={TAP} onClick={onRetry}>Try again</button>}>
      Couldn&rsquo;t read stated prices — those assets show no AED figure.
    </StateLine>
  );
}
